/**
 * 내 사례철(품목분류사례 스크랩) API + 경량 공유 스토어.
 * 내 품목(watchlist, 세번 단위)과 별개 — 특정 결정례(case_id)를 근거철로 저장.
 * 상세 담기(★)·"내 사례철" 목록·홈 카드가 같은 items 스토어를 공유해 즉시 동기화.
 * watchlistApi 미러 구조 + full items 보유(목록 렌더용).
 */
import { useEffect, useState } from "react";

function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
export function isLoggedIn() {
  return !!token();
}
function authHeaders(json) {
  const h = {};
  const t = token();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}

export async function fetchSavedCases() {
  if (!isLoggedIn()) return [];
  try {
    const r = await fetch("/api/onto/saved-cases", { headers: authHeaders() });
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d.items) ? d.items : [];
  } catch {
    return [];
  }
}
async function apiSave(payload) {
  return fetch("/api/onto/saved-cases", {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  }).then((r) => r.ok);
}
async function apiRemove(caseId) {
  return fetch(`/api/onto/saved-cases/${encodeURIComponent(caseId)}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then((r) => r.ok);
}

// ── 공유 스토어 (full items) ──
let _items = null; // Array | null(미로드)
const _subs = new Set();
const _emit = () => _subs.forEach((fn) => fn());

export async function ensureLoaded() {
  if (_items) return _items;
  _items = await fetchSavedCases();
  _emit();
  return _items;
}
export function getItems() {
  return _items || [];
}
export function hasCase(caseId) {
  return !!(_items && _items.some((i) => i.case_id === caseId));
}
export function invalidate() {
  _items = null;
}

// meta = {hsk, title, source, decision_date}
export async function toggleSaveCase(caseId, meta = {}) {
  if (!caseId) return false;
  await ensureLoaded();
  const has = hasCase(caseId);
  // 낙관적 갱신
  if (has) {
    _items = _items.filter((i) => i.case_id !== caseId);
  } else {
    _items = [{ case_id: caseId, note: "", created_at: null, ...meta }, ..._items];
  }
  _emit();
  const ok = has
    ? await apiRemove(caseId)
    : await apiSave({ case_id: caseId, ...meta });
  if (!ok) {
    // 롤백 = 서버 진실로 재동기화
    invalidate();
    await ensureLoaded();
  }
  return !has;
}

// 메모 저장(선택) — upsert(POST)로 note 만 갱신.
export async function saveNote(caseId, note) {
  if (!caseId) return false;
  await ensureLoaded();
  _items = _items.map((i) => (i.case_id === caseId ? { ...i, note } : i));
  _emit();
  const ok = await apiSave({ case_id: caseId, note: note == null ? "" : note });
  if (!ok) {
    invalidate();
    await ensureLoaded();
  }
  return ok;
}

// ── React 훅 ──
export function useSavedCases() {
  const [, force] = useState(0);
  useEffect(() => {
    ensureLoaded();
    const unsub = () => force((x) => x + 1);
    _subs.add(unsub);
    return () => _subs.delete(unsub);
  }, []);
  return {
    loggedIn: isLoggedIn(),
    items: getItems(),
    has: hasCase,
    count: getItems().length,
    toggle: toggleSaveCase,
    saveNote,
    reload: async () => {
      invalidate();
      await ensureLoaded();
    },
  };
}
